import { useChainSelectAll } from '../use-chain-select-all'
import { useTextLinking } from '../use-text-linking'
import { useTextLinkingToolResets } from '../use-text-linking-tool-resets'
import type { CanvasFoundation, CanvasToolModes } from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * Text-frame linking: linking mode and its port handlers, chain select-all, and dropping linking
 * mode when a creation tool takes over.
 */
export function useCanvasTextLinking(
  {
    linkedTextChains,
    checkpointLoadKey,
    onTextFramesLinked,
    onTextFramesUnlinked,
  }: ResolvedCenterCanvasConfig,
  {
    internalShapeManagerRef,
    unifiedLayerIdsRef,
  }: Pick<CanvasFoundation, 'internalShapeManagerRef' | 'unifiedLayerIdsRef'>,
  { isInShapeCreationMode, isInPenToolMode, isInTextCreationMode }: CanvasToolModes
) {
  const {
    textLinking,
    setTextLinking,
    textLinkingRevision,
    setTextLinkingRevision,
    isTextLinkingActive,
    textToolHoverFrameId,
    setTextToolHoverFrameId,
    linkedTextDragStartRef,
    textLinkAnnouncement,
    textLinkAriaKey,
    announceTextLink,
    cancelTextLinking,
    handleTextOutPortClick,
    handleTextInPortClick,
    handleTextUnlinkClick,
    handleTextPortHoverChange,
    findLinkableFrameAtDocPoint,
  } = useTextLinking({
    internalShapeManagerRef,
    unifiedLayerIdsRef,
    checkpointLoadKey,
    onTextFramesLinked,
    onTextFramesUnlinked,
  })

  const { handleSelectChainMembers } = useChainSelectAll({
    internalShapeManagerRef,
    linkedTextChains,
  })

  useTextLinkingToolResets({
    isInShapeCreationMode,
    isInPenToolMode,
    isInTextCreationMode,
    isTextLinkingActive,
    setTextLinking,
    setTextToolHoverFrameId,
  })

  return {
    textLinking,
    setTextLinking,
    textLinkingRevision,
    setTextLinkingRevision,
    isTextLinkingActive,
    textToolHoverFrameId,
    setTextToolHoverFrameId,
    linkedTextDragStartRef,
    textLinkAnnouncement,
    textLinkAriaKey,
    announceTextLink,
    cancelTextLinking,
    handleTextOutPortClick,
    handleTextInPortClick,
    handleTextUnlinkClick,
    handleTextPortHoverChange,
    findLinkableFrameAtDocPoint,
    handleSelectChainMembers,
  }
}
